import { DateTime, WeekdayNumbers } from 'luxon'

import { SanityCart } from '@data/sanity/queries/types/site'
import { Locale } from './language'

/**
 * Get order processing date based on current day and order processing day.
 */
export const getOrderProcessingDate = (
  locale: Locale,
  orderProcessingDay: WeekdayNumbers,
) => {
  const now = DateTime.now().setLocale(locale)

  const orderProcessingDate =
    now.weekday >= orderProcessingDay
      ? now
          .plus({
            days: 7,
          })
          .set({
            weekday: orderProcessingDay,
          }) // Next week's order processing date
      : now.set({
          weekday: orderProcessingDay,
        }) // Current week's order processing date

  return orderProcessingDate
}

/**
 * Get last order date based on order processing date.
 */
export const getLastOrderDate = (orderProcessingDate: DateTime) => {
  return orderProcessingDate.minus({
    days: 1,
  })
}

/**
 * Get estimated delivery date based on the order processing day and delivery time in days.
 */
export const getEstimatedDeliveryDate = (
  locale: Locale,
  orderProcessingDay: WeekdayNumbers,
  deliveryTimeInDays: number,
) => {
  const orderProcessingDate = getOrderProcessingDate(locale, orderProcessingDay)
  // TODO: Exclude weekends?
  const deliveryDate = orderProcessingDate.plus({
    days: deliveryTimeInDays,
  })

  return deliveryDate
}

/**
 * Gets text with added shipping dates in place of variable placeholders.
 */
export const getTextWithShippingDates = (
  locale: Locale,
  cartSettings: SanityCart,
  text: string,
) => {
  // Order processing date
  const orderProcessingDate =
    typeof cartSettings.shipping.orderProcessingDay !== 'undefined'
      ? getOrderProcessingDate(
          locale,
          cartSettings.shipping.orderProcessingDay as WeekdayNumbers,
        )
      : null
  const orderProcessingDateString = orderProcessingDate
    ? orderProcessingDate.toLocaleString(DateTime.DATE_HUGE, {
        locale,
      })
    : '-'
  const orderProcessingDateShortString = orderProcessingDate
    ? orderProcessingDate.toFormat('dd/MM')
    : '-'

  // Delivery date
  const deliveryDate =
    typeof cartSettings.shipping.orderProcessingDay !== 'undefined' &&
    typeof cartSettings.shipping.deliveryTimeInDays !== 'undefined'
      ? getEstimatedDeliveryDate(
          locale,
          cartSettings.shipping.orderProcessingDay as WeekdayNumbers,
          cartSettings.shipping.deliveryTimeInDays,
        )
      : null
  const deliveryDateString = deliveryDate
    ? deliveryDate.toLocaleString(DateTime.DATE_HUGE, {
        locale,
      })
    : '-'
  const deliveryDateShortString = deliveryDate
    ? deliveryDate.toFormat('dd/MM')
    : '-'

  // Last order date
  const lastOrderDate = orderProcessingDate
    ? getLastOrderDate(orderProcessingDate)
    : null
  const lastOrderDateString = lastOrderDate
    ? lastOrderDate.toLocaleString(DateTime.DATE_HUGE, {
        locale,
      })
    : '-'
  const lastOrderDateShortString = lastOrderDate
    ? lastOrderDate.toFormat('dd/MM')
    : '-'

  return text
    .replace(/{order_processing_date}/gi, orderProcessingDateString)
    .replace(/{order_processing_date_short}/gi, orderProcessingDateShortString)
    .replace(/{delivery_date}/gi, deliveryDateString)
    .replace(/{delivery_date_short}/gi, deliveryDateShortString)
    .replace(/{last_order_date}/gi, lastOrderDateString)
    .replace(/{last_order_date_short}/gi, lastOrderDateShortString)
}
